import clsx from "clsx";
import { FC, useContext, useEffect, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { DropdownSourceContext } from "@ryde/redux";
import {
  INeighborAreaDto,
  Log,
  onActivateKeyDown,
  RidesApi,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";

import { AreaTooltipStyled } from "./area-tooltip.styled";

interface IAreaTooltip {
  areaId: number | undefined;
  areaTitle: string;
  areaGroupName?: string;
  rideId: number;
}

export const AreaTooltip: FC<IAreaTooltip> = ({
  areaId,
  rideId,
  areaTitle,
  areaGroupName,
}) => {
  const { t } = useTranslation();
  const { getDropdownLabel } = useContext(DropdownSourceContext);
  const [neighborAreaDtos, setNeighborAreaDtos] = useState<
    INeighborAreaDto[] | undefined
  >(undefined);
  const [, startTransition] = useTransition();
  const { openEditFormDrawer } = useDrawers();
  const { hasPermissions: updateAreaPermission } = useHasPermissions({
    entity: "area",
    action: "update",
  });
  const handleAreaClick = useEvent((): void => {
    if (updateAreaPermission && areaId) {
      openEditFormDrawer({
        entity: "area",
        id: areaId,
      });
    }
  });

  useEffect(() => {
    const fetchNeighbor = async (
      fetchAreaId: number,
      fetchRideId: number,
    ): Promise<void> => {
      const nearbyApiResult = await RidesApi.searchNeighborAreasWithMaxPlaces({
        rideId: fetchRideId,
        areaId: fetchAreaId,
      });

      if (nearbyApiResult?.success && nearbyApiResult.data?.length) {
        const areaDtos = nearbyApiResult.data;
        Log.debug(`Found nearby areas  for areaId ${areaId}`, areaDtos);
        setNeighborAreaDtos(areaDtos);
      } else {
        Log.debug(
          `Not found neighbor areas for rideId ${rideId}, areaId ${areaId}`,
        );
      }
    };

    if (areaId && rideId) {
      startTransition(() => {
        fetchNeighbor(areaId, rideId);
      });
    }
  }, [areaId, getDropdownLabel, rideId]);

  if (!areaId) {
    return null;
  }

  return (
    <AreaTooltipStyled>
      {areaTitle} ID:{areaId}
      {!!areaGroupName && ` (${areaGroupName})`}
      {!!neighborAreaDtos?.length && (
        <p className="area-tooltip__title">{t("area.formLabel.areaIds")}:</p>
      )}
      {neighborAreaDtos?.map(neighborAreaDto => (
        <div
          key={neighborAreaDto.id}
          className={clsx({
            "truncate-text": true,
            pointer: updateAreaPermission,
          })}
          onClick={handleAreaClick}
          onKeyDown={onActivateKeyDown(handleAreaClick)}
          role="button"
          tabIndex={0}
        >
          {neighborAreaDto.name}{" "}
          {!!neighborAreaDto.areasGroupName &&
            `(${neighborAreaDto.areasGroupName})`}{" "}
          ID:{neighborAreaDto.id}
          {!!neighborAreaDto.maxPlaces && (
            <span>[{neighborAreaDto.maxPlaces} max]</span>
          )}
        </div>
      ))}
    </AreaTooltipStyled>
  );
};
